/**
 * Works out which column of a pasted file is which.
 *
 * Guest lists arrive in whatever shape the tool that made them chose: Partiful
 * writes "Name" and "Is Plus One Of", Google Contacts writes "Given Name" and
 * "E-mail 1 - Value", a wedding site writes "Guest First Name", and a host with
 * a spreadsheet writes whatever they felt like. Requiring one exact layout means
 * the host reformats the file by hand, which is where addresses get transposed.
 *
 * Detection is deliberately deterministic — a synonym table, not a model. It is
 * predictable, works offline, costs nothing, and never sends a guest's details
 * anywhere. What makes the feature usable is not the cleverness of the guess but
 * that the host SEES the guess and can change it before anything is written, so
 * a wrong column is a dropdown away rather than a support conversation.
 *
 * If a fallback for genuinely novel headers is ever wanted, it belongs behind
 * `detectMapping` and must map from the header text and the SHAPE summaries
 * below — never from cell values. No guest's address needs to leave the browser
 * to learn that column three is an email.
 */

export type GuestField =
  | 'tripsbyId'
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'status'
  | 'rsvpAt'
  | 'invitedBy'
  | 'plusOneOf'
  | 'tags'
  | 'dietary'
  | 'notes';

export interface FieldSpec {
  field: GuestField;
  label: string;
  /** Shown under the dropdown so the host knows what the column feeds. */
  hint: string;
}

/** The order these appear in the mapping UI: identity first, then contact. */
export const GUEST_FIELDS: readonly FieldSpec[] = [
  { field: 'tripsbyId', label: 'Tripsby ID', hint: 'Only in a file exported from here. Matches rows exactly.' },
  { field: 'name', label: 'Full name', hint: 'Or map First and Last name separately.' },
  { field: 'firstName', label: 'First name', hint: 'Combined with Last name when there is no full-name column.' },
  { field: 'lastName', label: 'Last name', hint: 'Combined with First name.' },
  { field: 'email', label: 'Email', hint: 'What invites are sent to.' },
  { field: 'phone', label: 'Phone', hint: 'Used when there is no email.' },
  { field: 'status', label: 'RSVP status', hint: 'Leave unmapped to keep the answers already on file.' },
  { field: 'rsvpAt', label: 'RSVP date', hint: 'When they answered.' },
  { field: 'invitedBy', label: 'Invited by', hint: 'Attribution only.' },
  { field: 'plusOneOf', label: 'Plus one of', hint: 'Nests this guest under their inviter.' },
  /*
    Tags and dietary sit ABOVE notes deliberately. `detectMapping` never steals a
    column a more specific field already claimed, and it walks this list in
    order — so whichever of them is declared first wins a header called
    "Dietary". Notes used to carry "dietary" as a synonym and therefore swallowed
    that column, which is how a whole roster's allergies arrived as free text
    nobody could count.
  */
  {
    field: 'tags',
    label: 'Tags / groups',
    hint: 'Split on ; | or , — "college friends; rugby" becomes two tags.',
  },
  {
    field: 'dietary',
    label: 'Dietary & access',
    hint: 'Allergies, diets, mobility. Host-only — never shown to guests.',
  },
  { field: 'notes', label: 'Notes', hint: 'Free text kept against the guest.' },
];

/**
 * The fields this API can actually store.
 *
 * Studio and the concierge API deploy separately, and an API that predates
 * guest tags reads no `tagSlugs` off an import row. Offering the host a Tags
 * dropdown against one of those would let them map a column, confirm it, and
 * have it dropped in silence — the same failure the notes column has been
 * quietly performing all along. So the dropdown is not offered rather than
 * offered inert, and detection is asked the same question so it cannot fill in
 * a field the mapper will never show.
 */
export function guestFieldsFor(supportsTags: boolean): readonly FieldSpec[] {
  if (supportsTags) return GUEST_FIELDS;
  return GUEST_FIELDS.filter((spec) => spec.field !== 'tags' && spec.field !== 'dietary');
}

/**
 * Header synonyms, lowercased and punctuation-stripped before comparison.
 *
 * Ordered most specific first within each field: "email address" has to beat a
 * bare "address", and Google's "e-mail 1 - value" normalizes to "email 1 value",
 * which is why the prefix rule below exists rather than exact matching alone.
 */
const SYNONYMS: Record<GuestField, readonly string[]> = {
  tripsbyId: ['tripsby id', 'tripsbyid'],
  name: ['full name', 'guest name', 'name', 'guest', 'attendee', 'display name'],
  firstName: ['first name', 'given name', 'firstname', 'first', 'guest first name'],
  lastName: ['last name', 'family name', 'surname', 'lastname', 'last', 'guest last name'],
  email: ['email address', 'e mail address', 'email 1 value', 'email', 'e mail', 'mail'],
  phone: ['phone number', 'mobile number', 'phone 1 value', 'phone', 'mobile', 'cell', 'telephone'],
  status: ['rsvp status', 'rsvp', 'status', 'attending', 'response'],
  rsvpAt: ['rsvp date', 'responded at', 'response date', 'rsvp at'],
  invitedBy: ['invited by', 'host', 'inviter'],
  plusOneOf: ['is plus one of', 'plus one of', 'guest of', 'plus one'],
  tags: ['tags', 'tag', 'groups', 'group', 'category', 'segment', 'side', 'table group'],
  dietary: [
    'dietary requirements', 'dietary', 'diet', 'allergies', 'allergy',
    'food', 'restrictions', 'accessibility', 'access',
  ],
  // "dietary" is NOT here any more — see the comment in GUEST_FIELDS. A notes
  // column that swallows the allergy column turns a countable set into prose.
  notes: ['notes', 'note', 'comments', 'preferences'],
};

/** Lowercase, strip punctuation, collapse whitespace. "E-mail 1 - Value" → "email 1 value". */
export function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export type ColumnMapping = Partial<Record<GuestField, number>>;

export interface ColumnShape {
  index: number;
  header: string;
  /** Non-empty sample values, used for shape checks — never sent anywhere. */
  filled: number;
  sampled: number;
  looksLikeEmail: number;
  looksLikePhone: number;
}

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Enough digits to be dialable; deliberately loose about punctuation and country codes. */
function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

export function summarizeColumns(headers: string[], rows: string[][]): ColumnShape[] {
  return headers.map((header, index) => {
    const values = rows.map((row) => (row[index] ?? '').trim()).filter(Boolean);
    return {
      index,
      header,
      filled: values.length,
      sampled: rows.length,
      looksLikeEmail: values.filter((v) => EMAIL_SHAPE.test(v)).length,
      looksLikePhone: values.filter(looksLikePhone).length,
    };
  });
}

/**
 * Best guess at the mapping.
 *
 * Header text decides it wherever it can, because a header is what the host
 * themselves would read. Shape is only consulted to fill a field the headers did
 * not name — a file with no header row at all, or one whose columns are called
 * "Column 1". A column already claimed by a more specific field is never stolen.
 */
export function detectMapping(
  headers: string[],
  rows: string[][],
  fields: readonly FieldSpec[] = GUEST_FIELDS,
): ColumnMapping {
  const normalized = headers.map(normalizeHeader);
  const mapping: ColumnMapping = {};
  const taken = new Set<number>();

  const free = (i: number) => !taken.has(i);

  for (const { field } of fields) {
    // Every exact match is tried before any prefix match, across all of the
    // field's synonyms. The other way round, a loose synonym swallows a column
    // that a later one names outright: "rsvp" prefix-matches "RSVP Date", so
    // Partiful's own "Status" column never got the chance to match itself.
    let index = -1;
    for (const synonym of SYNONYMS[field]) {
      index = normalized.findIndex((h, i) => free(i) && h === synonym);
      if (index >= 0) break;
    }
    if (index < 0) {
      for (const synonym of SYNONYMS[field]) {
        // "email 1 value" and "email 2 value" both start with "email"; the first
        // wins, which matches how contact exports order them.
        index = normalized.findIndex((h, i) => free(i) && h.startsWith(`${synonym} `));
        if (index >= 0) break;
      }
    }
    if (index >= 0) {
      mapping[field] = index;
      taken.add(index);
    }
  }

  // Shape only fills what the headers left unnamed.
  const shapes = summarizeColumns(headers, rows);
  const majority = (count: number, of: number) => of > 0 && count / of > 0.6;

  if (mapping.email === undefined) {
    const guess = shapes.find((s) => !taken.has(s.index) && majority(s.looksLikeEmail, s.filled));
    if (guess) {
      mapping.email = guess.index;
      taken.add(guess.index);
    }
  }
  if (mapping.phone === undefined) {
    const guess = shapes.find((s) => !taken.has(s.index) && majority(s.looksLikePhone, s.filled));
    if (guess) {
      mapping.phone = guess.index;
      taken.add(guess.index);
    }
  }

  return mapping;
}

export interface MappingWarning {
  field: GuestField;
  message: string;
}

/**
 * What looks wrong about a mapping, in the host's terms.
 *
 * These are warnings, never blocks. The host can see the file and we cannot —
 * a column of work addresses with two malformed rows is still the email column.
 * The one hard requirement is a name, because a row without one cannot become a
 * guest at all.
 */
export function validateMapping(
  mapping: ColumnMapping,
  headers: string[],
  rows: string[][],
): { errors: string[]; warnings: MappingWarning[] } {
  const errors: string[] = [];
  const warnings: MappingWarning[] = [];
  const shapes = summarizeColumns(headers, rows);
  const shapeOf = (index?: number) =>
    index === undefined ? undefined : shapes.find((s) => s.index === index);

  const hasName = mapping.name !== undefined;
  const hasSplitName = mapping.firstName !== undefined || mapping.lastName !== undefined;
  if (!hasName && !hasSplitName) {
    errors.push('Pick the column holding each guest’s name. Without it there is nobody to import.');
  }

  const email = shapeOf(mapping.email);
  if (email && email.filled > 0 && email.looksLikeEmail / email.filled < 0.5) {
    warnings.push({
      field: 'email',
      message: `Most values in "${email.header}" don’t look like email addresses. Check this is the right column — importing the wrong one overwrites addresses you already have.`,
    });
  }

  const phone = shapeOf(mapping.phone);
  if (phone && phone.filled > 0 && phone.looksLikePhone / phone.filled < 0.5) {
    warnings.push({
      field: 'phone',
      message: `Most values in "${phone.header}" don’t look like phone numbers.`,
    });
  }

  if (mapping.status === undefined) {
    warnings.push({
      field: 'status',
      message: 'No RSVP column mapped, so the answers already on file stay as they are. Map one only if this file has newer answers than Tripsby does.',
    });
  }

  return { errors, warnings };
}

export interface MappedRow {
  tripsbyId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  status: string | null;
  rsvpAt: string | null;
  invitedBy: string | null;
  plusOneOf: string | null;
  /** Raw cell text. Splitting it into tag labels is `splitTagCell`'s job. */
  tags: string | null;
  dietary: string | null;
  notes: string | null;
}

/**
 * Applies a mapping to the data rows. Deterministic and local: whatever the
 * mapping came from, turning rows into guests is plain indexing, so a column the
 * host corrected behaves exactly like one that was detected.
 */
export function applyMapping(mapping: ColumnMapping, rows: string[][]): {
  rows: MappedRow[];
  skipped: { line: number; reason: string }[];
} {
  const mapped: MappedRow[] = [];
  const skipped: { line: number; reason: string }[] = [];
  const at = (row: string[], index?: number) =>
    index === undefined ? '' : (row[index] ?? '').trim();

  rows.forEach((row, offset) => {
    // Line 1 is the header the host saw, so data starts at 2.
    const line = offset + 2;
    const whole = at(row, mapping.name);
    const first = at(row, mapping.firstName);
    const last = at(row, mapping.lastName);
    // A full-name column wins; otherwise the halves join. Either half alone is
    // still a name — "Cher" should not be dropped for having no surname.
    const name = whole || [first, last].filter(Boolean).join(' ').trim();

    if (!name) {
      skipped.push({ line, reason: 'Row has no name.' });
      return;
    }

    mapped.push({
      tripsbyId: at(row, mapping.tripsbyId) || null,
      name,
      email: at(row, mapping.email) || null,
      phone: at(row, mapping.phone) || null,
      status: at(row, mapping.status) || null,
      rsvpAt: at(row, mapping.rsvpAt) || null,
      invitedBy: at(row, mapping.invitedBy) || null,
      plusOneOf: at(row, mapping.plusOneOf) || null,
      tags: at(row, mapping.tags) || null,
      dietary: at(row, mapping.dietary) || null,
      notes: at(row, mapping.notes) || null,
    });
  });

  return { rows: mapped, skipped };
}
